import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { toast } from "sonner";
import { BadgeCheck, ShieldAlert } from "lucide-react";
import useDocumentTitle from "../hooks/useDocumentTitle";
import ChangePasswordCard from "../components/ChangePasswordCard";
import AvatarUpload from "../components/AvatarUpload";

export default function EmployerProfile() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    company_name: "", company_website: "", company_size: "", industry: "", about: "",
  });
  useDocumentTitle("Company profile");

  useEffect(() => {
    api.get("/profile/employer").then(({ data }) => {
      if (data) setData({
        company_name: data.company_name || "",
        company_website: data.company_website || "",
        company_size: data.company_size || "",
        industry: data.industry || "",
        about: data.about || "",
      });
    }).finally(() => setLoading(false));
  }, []);

  const save = async () => {
    if (!data.company_name) { toast.error("Company name is required"); return; }
    setSaving(true);
    try {
      await api.put("/profile/employer", data);
      toast.success("Company profile saved");
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen"><Header /><div className="p-10 text-sm text-zinc-500">Loading...</div></div>;

  return (
    <div className="min-h-screen bg-zinc-50">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-[#002FA7] font-bold mb-1">company profile</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-zinc-950">Tell candidates about your company</h1>
        </div>

        {user?.is_verified_employer ? (
          <Card className="p-4 mb-6 border-blue-200 bg-blue-50" data-testid="verified-employer-banner">
            <div className="flex items-start gap-3">
              <span className="h-9 w-9 rounded-md bg-white grid place-items-center text-[#002FA7] flex-shrink-0 border border-blue-200">
                <BadgeCheck className="h-5 w-5" />
              </span>
              <div className="text-sm">
                <div className="font-semibold text-zinc-950 flex items-center gap-1.5">
                  Verified employer
                  <span className="inline-flex items-center gap-1 text-[10px] tag-mono px-1.5 py-0.5 rounded-full bg-blue-100 text-[#002FA7] border border-blue-200">
                    <BadgeCheck className="h-3 w-3" /> verified
                  </span>
                </div>
                <p className="text-zinc-700 mt-1">
                  Your company is verified. Candidates see the verified badge on job listings, and you can view seeker phone numbers to reach out directly.
                </p>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="p-4 mb-6 border-amber-200 bg-amber-50" data-testid="unverified-employer-banner">
            <div className="flex items-start gap-3">
              <span className="h-9 w-9 rounded-md bg-white grid place-items-center text-amber-700 flex-shrink-0 border border-amber-200">
                <ShieldAlert className="h-5 w-5" />
              </span>
              <div className="text-sm">
                <div className="font-semibold text-zinc-950">Get your company verified</div>
                <p className="text-zinc-700 mt-1">
                  Verified employers get a trust badge on job listings and can see candidate phone numbers. To request verification, fill this profile completely and{" "}
                  <a href="/contact?purpose=partnership" className="underline text-[#002FA7] hover:text-[#00227A]">contact our team</a>.
                </p>
              </div>
            </div>
          </Card>
        )}

        <Card className="p-6 space-y-4 mb-6">
          <h2 className="font-display text-lg font-bold tracking-tight">Company logo / profile picture</h2>
          <AvatarUpload userId={user?.user_id} fallback={user?.name || data.company_name || user?.email} />
        </Card>

        <Card className="p-6 space-y-4">
          <div>
            <Label>Company name *</Label>
            <Input value={data.company_name} onChange={(e)=>setData(d=>({...d, company_name: e.target.value}))} className="mt-1.5" data-testid="emp-company-name" />
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Website</Label>
              <Input value={data.company_website} onChange={(e)=>setData(d=>({...d, company_website: e.target.value}))} className="mt-1.5" placeholder="https://" data-testid="emp-website" />
            </div>
            <div>
              <Label>Company size</Label>
              <select
                value={data.company_size}
                onChange={(e)=>setData(d=>({...d, company_size: e.target.value}))}
                className="mt-1.5 w-full h-10 rounded-md border border-zinc-200 bg-white px-3 text-sm"
                data-testid="emp-size"
              >
                <option value="">Select…</option>
                <option>1–10</option>
                <option>11–50</option>
                <option>51–200</option>
                <option>201–500</option>
                <option>501–1000</option>
                <option>1000+</option>
              </select>
            </div>
            <div>
              <Label>Industry</Label>
              <Input value={data.industry} onChange={(e)=>setData(d=>({...d, industry: e.target.value}))} className="mt-1.5" placeholder="Fintech, Healthcare, …" data-testid="emp-industry" />
            </div>
          </div>
          <div>
            <Label>About</Label>
            <Textarea rows={4} value={data.about} onChange={(e)=>setData(d=>({...d, about: e.target.value}))} className="mt-1.5" placeholder="What does your company do?" data-testid="emp-about" />
          </div>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="save-employer-btn" className="bg-[#002FA7] hover:bg-[#00227A] text-white">
              {saving ? "Saving..." : "Save profile"}
            </Button>
          </div>
        </Card>

        <div className="mt-8">
          <div className="mb-3 text-xs uppercase tracking-widest text-zinc-500 font-bold tag-mono">account security</div>
          <ChangePasswordCard />
        </div>
      </main>
    </div>
  );
}
